import { delay, put, call, take, takeEvery, select } from "typed-redux-saga";
import { WebConfigurationClient } from "../../web-apis/web-configuration";
import { appVersionSet, labelingFetched, labelingSet, newVersionAlertSnoozed, newVersionAlertSnoozeSet, workspaceLabelingFetched, workspaceLabelingSet } from "./appVersionSlice";
import { appInitializationFailed } from "../appStatus/appStatusSlice";
import { NEW_VERSION_SNOOZE_TIME_IN_HOURS } from "./appVersionTypes";
import { callApi } from "../sagas";
import { getApiErrorMessage } from "../../util/errors";
import { appConfigSelectors } from "../appConfig/appConfigSlice";
import { convertSupportedModelTypeToActualModelType } from "../appConfig/appDeploymentInfoTypes";


// wait for 2 hours before doing another version check
const WAIT_TIME_BETWEEN_VERSION_CHECKS_MS = 2 * 60 * 60 * 1000;


export function* watchServerVersionPeriodically() {
    while (true) {
        yield* delay(WAIT_TIME_BETWEEN_VERSION_CHECKS_MS);
        yield* call(getAppVersion);
    }
}

export function* getAppVersion() {
    try {
        const appVersion = yield* call(WebConfigurationClient.getAppVersionInfo);
        yield* put(appVersionSet(appVersion));
    }
    catch (error) {
        console.log('An error occurred when trying to retrieve app version:');
        console.error(error);
        yield* put(appInitializationFailed((error as Error).toString()));
    }
}

function* fetchLabelingSaga() {
    const appDeploymentInfo = yield* select(appConfigSelectors.selectAppDeploymentInfo);
    const modelTypes = appDeploymentInfo?.supportedModelTypes.map(s => convertSupportedModelTypeToActualModelType(s)) || [];

    yield* callApi({
        doApiCall: function* (client) {
            const productLabel = yield* call(async () => client.fetchLabelingAsync(modelTypes));
            return productLabel;
        },
        onSuccess: function* (result) {
            yield* put(labelingSet({ combinedLabeling: result }));
        },
        onFailure: function* (error) {
            console.error('Was not able to fetch product label');
            const errorMessage = getApiErrorMessage(error);
            yield* put(labelingSet({ combinedLabeling: null, error: errorMessage }));
        }
    });
}

function* fetchWorkspaceLabelingSaga() {
    yield* callApi({
        doApiCall: function* (client) {
            const productLabel = yield* call(async () => client.fetchWorkspaceLabelingAsync());
            return productLabel;
        },
        onSuccess: function* (result) {
            yield* put(workspaceLabelingSet({ labeling: result }));
        },
        onFailure: function* (error) {
            console.error('Was not able to fetch workspace labeling');
            const errorMessage = getApiErrorMessage(error);
            yield* put(workspaceLabelingSet({ labeling: null, error: errorMessage }));
        }
    });
}
function* watchVersionAlertSnoozeSaga() {
    while (true) {
        yield* take(newVersionAlertSnoozed);
        const snoozeForMs = NEW_VERSION_SNOOZE_TIME_IN_HOURS * 60 * 60 * 1000;
        yield* put(newVersionAlertSnoozeSet(true));
        yield* delay(snoozeForMs)
        yield* put(newVersionAlertSnoozeSet(false));
    }
}

function* watchFetchLabelingSaga() {
    yield* takeEvery(labelingFetched, fetchLabelingSaga);
}

function* watchFetchWorkspaceLabelingSaga() {
    yield* takeEvery(workspaceLabelingFetched, fetchWorkspaceLabelingSaga);
}

/** Returns all relevant watches to be added to a main root watch saga */
export function getWatchesForAppVersionSagas() {
    return [
        // watchServerVersionPeriodically,  // this one is handled separately in main app initialization saga
        watchVersionAlertSnoozeSaga(),
        watchFetchLabelingSaga(),
        watchFetchWorkspaceLabelingSaga(),
    ];
}
